import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { executeAction } from '../../utils/actionExecutor';

/**
 * FooterPreview - Main preview component for footer
 * Renders different footer styles based on configuration
 */
export const FooterPreview = ({ component, theme, onClick, isSelected }) => {
  const { commonAttrs, specificAttrs } = component;
  const {
    footerStyle = 'multi-column',
    columns = [],
    copyrightText = '© 2024 Your Company. All rights reserved.',
    showSocialLinks = true,
    socialLinks = [],
    backgroundColor = '#1F2937',
    textColor = '#D1D5DB',
    linkColor = '#FFFFFF',
    linkHoverColor = '#3B82F6'
  } = specificAttrs;

  // Container style with common attributes
  const containerStyle = {
    padding: `${commonAttrs?.paddingTop || 16}px ${commonAttrs?.paddingRight || 16}px ${commonAttrs?.paddingBottom || 16}px ${commonAttrs?.paddingLeft || 16}px`,
    margin: `${commonAttrs?.marginTop || 8}px ${commonAttrs?.marginRight || 0}px ${commonAttrs?.marginBottom || 8}px ${commonAttrs?.marginLeft || 0}px`,
    width: commonAttrs?.width === 'auto' ? 'auto' : `${commonAttrs?.width || 100}%`,
    cursor: onClick ? 'pointer' : 'default',
    border: isSelected ? '2px solid #3B82F6' : '2px solid transparent',
    borderRadius: '4px'
  };

  // Render based on footer style
  switch (footerStyle) {
    case 'multi-column':
      return (
        <div style={containerStyle} onClick={onClick}>
          <MultiColumnFooter
            columns={columns}
            copyrightText={copyrightText}
            showSocialLinks={showSocialLinks}
            socialLinks={socialLinks}
            backgroundColor={backgroundColor}
            textColor={textColor}
            linkColor={linkColor}
            linkHoverColor={linkHoverColor}
          />
        </div>
      );

    case 'simple-centered':
      return (
        <div style={containerStyle} onClick={onClick}>
          <SimpleCenteredFooter
            columns={columns}
            copyrightText={copyrightText}
            showSocialLinks={showSocialLinks}
            socialLinks={socialLinks}
            backgroundColor={backgroundColor}
            textColor={textColor}
            linkColor={linkColor}
            linkHoverColor={linkHoverColor}
          />
        </div>
      );

    case 'minimal':
      return (
        <div style={containerStyle} onClick={onClick}>
          <MinimalFooter
            copyrightText={copyrightText}
            showSocialLinks={showSocialLinks}
            socialLinks={socialLinks}
            backgroundColor={backgroundColor}
            textColor={textColor}
          />
        </div>
      );

    case 'newsletter':
      return (
        <div style={containerStyle} onClick={onClick}>
          <NewsletterFooter
            columns={columns}
            copyrightText={copyrightText}
            showSocialLinks={showSocialLinks}
            socialLinks={socialLinks}
            backgroundColor={backgroundColor}
            textColor={textColor}
            linkColor={linkColor}
            linkHoverColor={linkHoverColor}
          />
        </div>
      );

    default:
      return (
        <div style={containerStyle} onClick={onClick}>
          <MultiColumnFooter
            columns={columns}
            copyrightText={copyrightText}
            showSocialLinks={showSocialLinks}
            socialLinks={socialLinks}
            backgroundColor={backgroundColor}
            textColor={textColor}
            linkColor={linkColor}
            linkHoverColor={linkHoverColor}
          />
        </div>
      );
  }
};

/**
 * MultiColumnFooter - Footer with multiple link columns
 */
const MultiColumnFooter = ({
  columns,
  copyrightText,
  showSocialLinks,
  socialLinks,
  backgroundColor,
  textColor,
  linkColor,
  linkHoverColor
}) => {
  return (
    <footer style={{
      backgroundColor,
      color: textColor,
      padding: '48px 24px 24px',
      width: '100%'
    }}>
      {/* Columns Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.min(columns.length || 1, 4)}, 1fr)`,
        gap: '32px',
        marginBottom: '40px',
        maxWidth: '1200px',
        margin: '0 auto 40px'
      }}>
        {columns.length === 0 ? (
          <div style={{
            gridColumn: 'span 4',
            textAlign: 'center',
            fontSize: '13px',
            fontStyle: 'italic',
            opacity: 0.6
          }}>
            Add footer columns to display links
          </div>
        ) : (
          columns.map((column, index) => (
            <FooterColumn
              key={column.id || index}
              column={column}
              textColor={textColor}
              linkColor={linkColor}
              linkHoverColor={linkHoverColor}
            />
          ))
        )}
      </div>

      {/* Bottom Section */}
      <div style={{
        borderTop: `1px solid ${textColor}33`,
        paddingTop: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Copyright */}
        <div style={{
          fontSize: '13px',
          opacity: 0.8
        }}>
          {copyrightText}
        </div>

        {/* Social Links */}
        {showSocialLinks && socialLinks.length > 0 && (
          <SocialLinks
            socialLinks={socialLinks}
            linkColor={linkColor}
            linkHoverColor={linkHoverColor}
          />
        )}
      </div>
    </footer>
  );
};

/**
 * SimpleCenteredFooter - Centered simple footer
 */
const SimpleCenteredFooter = ({
  columns,
  copyrightText,
  showSocialLinks,
  socialLinks,
  backgroundColor,
  textColor,
  linkColor,
  linkHoverColor
}) => {
  // Flatten all links from all columns
  const allLinks = columns.flatMap(col => col.links || []);

  return (
    <footer style={{
      backgroundColor,
      color: textColor,
      padding: '40px 24px',
      textAlign: 'center',
      width: '100%'
    }}>
      {/* Links */}
      {allLinks.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: '24px',
          marginBottom: '24px'
        }}>
          {allLinks.slice(0, 8).map((link, index) => (
            <FooterLink
              key={link.id || index}
              link={link}
              linkColor={linkColor}
              linkHoverColor={linkHoverColor}
            />
          ))}
        </div>
      )}

      {/* Social Links */}
      {showSocialLinks && socialLinks.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <SocialLinks
            socialLinks={socialLinks}
            linkColor={linkColor}
            linkHoverColor={linkHoverColor}
          />
        </div>
      )}

      {/* Copyright */}
      <div style={{
        fontSize: '13px',
        opacity: 0.8
      }}>
        {copyrightText}
      </div>
    </footer>
  );
};

/**
 * MinimalFooter - Just copyright and social links
 */
const MinimalFooter = ({
  copyrightText,
  showSocialLinks,
  socialLinks,
  backgroundColor,
  textColor
}) => {
  return (
    <footer style={{
      backgroundColor,
      color: textColor,
      padding: '24px',
      textAlign: 'center',
      width: '100%',
      borderTop: `1px solid ${textColor}33`
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '24px',
        flexWrap: 'wrap'
      }}>
        {/* Copyright */}
        <div style={{
          fontSize: '13px',
          opacity: 0.8
        }}>
          {copyrightText}
        </div>

        {/* Social Links */}
        {showSocialLinks && socialLinks.length > 0 && (
          <SocialLinks
            socialLinks={socialLinks}
            linkColor={textColor}
            linkHoverColor="#3B82F6"
          />
        )}
      </div>
    </footer>
  );
};

/**
 * NewsletterFooter - Footer with newsletter signup
 */
const NewsletterFooter = ({
  columns,
  copyrightText,
  showSocialLinks,
  socialLinks,
  backgroundColor,
  textColor,
  linkColor,
  linkHoverColor
}) => {
  return (
    <footer style={{
      backgroundColor,
      color: textColor,
      padding: '48px 24px 24px',
      width: '100%'
    }}>
      {/* Newsletter Section */}
      <div style={{
        textAlign: 'center',
        marginBottom: '40px',
        maxWidth: '600px',
        margin: '0 auto 40px'
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '12px',
          color: textColor
        }}>
          Subscribe to our newsletter
        </h3>
        <p style={{
          fontSize: '14px',
          marginBottom: '20px',
          opacity: 0.8
        }}>
          Get the latest updates and news delivered to your inbox
        </p>
        <div style={{
          display: 'flex',
          gap: '8px',
          maxWidth: '400px',
          margin: '0 auto'
        }}>
          <input
            type="email"
            placeholder="Enter your email"
            style={{
              flex: 1,
              padding: '10px 16px',
              borderRadius: '6px',
              border: 'none',
              fontSize: '14px'
            }}
          />
          <button style={{
            padding: '10px 24px',
            backgroundColor: '#3B82F6',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer'
          }}>
            Subscribe
          </button>
        </div>
      </div>

      {/* Columns */}
      {columns.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(columns.length, 4)}, 1fr)`,
          gap: '32px',
          marginBottom: '40px',
          maxWidth: '1200px',
          margin: '0 auto 40px'
        }}>
          {columns.map((column, index) => (
            <FooterColumn
              key={column.id || index}
              column={column}
              textColor={textColor}
              linkColor={linkColor}
              linkHoverColor={linkHoverColor}
            />
          ))}
        </div>
      )}

      {/* Bottom Section */}
      <div style={{
        borderTop: `1px solid ${textColor}33`,
        paddingTop: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <div style={{ fontSize: '13px', opacity: 0.8 }}>
          {copyrightText}
        </div>
        {showSocialLinks && socialLinks.length > 0 && (
          <SocialLinks
            socialLinks={socialLinks}
            linkColor={linkColor}
            linkHoverColor={linkHoverColor}
          />
        )}
      </div>
    </footer>
  );
};

/**
 * FooterColumn - Individual footer column with links
 */
const FooterColumn = ({ column, textColor, linkColor, linkHoverColor }) => {
  return (
    <div>
      <h4 style={{
        fontSize: '14px',
        fontWeight: '600',
        marginBottom: '16px',
        color: textColor,
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        {column.title}
      </h4>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px'
      }}>
        {(!column.links || column.links.length === 0) ? (
          <span style={{
            fontSize: '12px',
            fontStyle: 'italic',
            opacity: 0.5
          }}>
            No links
          </span>
        ) : (
          column.links.map((link, index) => (
            <FooterLink
              key={link.id || index}
              link={link}
              linkColor={linkColor}
              linkHoverColor={linkHoverColor}
            />
          ))
        )}
      </div>
    </div>
  );
};

/**
 * FooterLink - Individual footer link
 */
const FooterLink = ({ link, linkColor, linkHoverColor }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = async (e) => {
    e.stopPropagation();

    // External URL takes precedence
    if (link.externalUrl) {
      if (link.openInNewTab) {
        window.open(link.externalUrl, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = link.externalUrl;
      }
      return;
    }

    // Navigate to target page
    if (link.targetPage) {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const appName = urlParams.get('app') || localStorage.getItem('currentAppName');

        if (appName) {
          const previewUrl = `/app-preview?app=${encodeURIComponent(appName)}&page=${encodeURIComponent(link.targetPage)}`;
          
          if (link.openInNewTab) {
            window.open(previewUrl, '_blank', 'noopener,noreferrer');
          } else {
            window.location.href = previewUrl;
          }
        }
      } catch (error) {
        console.error('❌ Navigation failed:', error);
      }
    }
    // Execute action
    else if (link.action) {
      try {
        console.log('🔗 Executing footer link action:', link.action);
        await executeAction(link.action);
      } catch (error) {
        console.error('❌ Action execution failed:', error);
      }
    }
  };

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: 'none',
        border: 'none',
        color: isHovered ? linkHoverColor : linkColor,
        fontSize: '13px',
        cursor: 'pointer',
        textAlign: 'left',
        padding: '0',
        transition: 'color 0.2s ease',
        textDecoration: isHovered ? 'underline' : 'none'
      }}
    >
      {link.label}
    </button>
  );
};

/**
 * SocialLinks - Social media icons
 */
const SocialLinks = ({ socialLinks, linkColor, linkHoverColor }) => {
  return (
    <div style={{
      display: 'flex',
      gap: '16px',
      justifyContent: 'center'
    }}>
      {socialLinks.map((social, index) => (
        <SocialLink
          key={social.id || index}
          social={social}
          linkColor={linkColor}
          linkHoverColor={linkHoverColor}
        />
      ))}
    </div>
  );
};

/**
 * SocialLink - Individual social media link
 */
const SocialLink = ({ social, linkColor, linkHoverColor }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Get icon component
  const IconComponent = Icons[social.icon] || Icons.Circle;

  const handleClick = (e) => {
    e.stopPropagation();
    if (social.url) {
      window.open(social.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: 'none',
        border: 'none',
        color: isHovered ? linkHoverColor : linkColor,
        cursor: social.url ? 'pointer' : 'default',
        padding: '8px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        opacity: social.url ? 1 : 0.5
      }}
    >
      <IconComponent size={20} />
    </button>
  );
};

export default FooterPreview;